const QUALITY_ASSESSMENT_BASE = '/cmx/quality-assessment/';

const toAbsolute = (path: string): string =>
  path.startsWith('/') ? path : `/cmx/${path}`;

const buildQaPath = (projectId: string, status: string | null): string => {
  const query = new URLSearchParams({ projectId });
  if (status) {
    query.set('status', status);
  }
  return `${QUALITY_ASSESSMENT_BASE}?${query.toString()}`;
};

const getProjectParams = (
  queryString: string
): { projectId: string | null; status: string | null } => {
  const params = new URLSearchParams(queryString);
  return { projectId: params.get('projectId'), status: params.get('status') };
};

/**
 * Resolves the grade-bench back-navigation target (CDMX-17399).
 *
 * The grading-screen URL carries projectId/status for the CURRENT work item
 * (set at launch), so it wins. Redux backToWorklistPath is a single global
 * string that can be missing (refresh), stale ('worklist/' after case-reopen
 * flows), or describe a different project — it is only a fallback, and only
 * when it carries a projectId. The result never leaves quality-assessment.
 */
export function resolveGradeBenchBackPath(
  backToWorklistPath: string | null | undefined,
  search: string
): string {
  const urlParams = getProjectParams(search);
  if (urlParams.projectId) {
    return buildQaPath(urlParams.projectId, urlParams.status);
  }

  const queryIndex = backToWorklistPath?.indexOf('?') ?? -1;
  const reduxParams = getProjectParams(
    queryIndex >= 0 ? backToWorklistPath!.slice(queryIndex) : ''
  );
  if (reduxParams.projectId) {
    return buildQaPath(reduxParams.projectId, reduxParams.status);
  }

  // Last resort: keep grade-bench users inside CQA — never bounce them to the
  // coding worklist off a clobbered redux path.
  if (
    backToWorklistPath &&
    toAbsolute(backToWorklistPath).startsWith(QUALITY_ASSESSMENT_BASE)
  ) {
    return toAbsolute(backToWorklistPath);
  }
  return QUALITY_ASSESSMENT_BASE;
}
